// Owner: shell-nav (plan §9.2, shell-ia F-07). The phone shell below 640 px: a bottom bar Home · Inbox ·
// Search · Library · Menu whose active tab always matches the route, and the Menu sheet with the rail
// model (workspaces, Ask AI, every object in nav, pinned, Automations for builders, every settings page
// the member may open) plus language, shortcuts and sign out — everything within two taps.
import { Link, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';
import { Icon } from '@dataroom/ui';
import { t } from '../lib/i18n';
import { useNav } from '../lib/queries';
import { appActiveKey, libraryPath } from './nav';
import { openCommandPalette } from './overlays';
import { useHasRoute } from './routes';

const MobileMenu = lazy(() => import('./MobileMenu'));

export function MobileTabBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = appActiveKey(pathname);
  const inbox = useNav().data?.badges.inbox;
  const hasRoute = useHasRoute();
  const [menu, setMenu] = useState(false);
  const library = libraryPath(hasRoute);
  const tab = (key: string) => `mobile-tab${active === key ? ' active' : ''}`;
  // The Menu tab is the active one on the destinations it leads to (objects, automations, settings,
  // account); a page of no tab (a 404) has no active tab.
  const menuActive =
    menu || Boolean(active && (active.startsWith('object:') || ['automations', 'settings'].includes(active)));
  return (
    <>
      <nav className="mobile-tabs" aria-label={t('shell.nav.mobile')} data-testid="mobile-tabs">
        <Link
          to="/"
          className={tab('home')}
          aria-current={active === 'home' ? 'page' : undefined}
          data-testid="mobile-tab-home"
        >
          <Icon name="home" size={20} />
          <span className="mobile-tab-label">{t('shell.nav.home')}</span>
        </Link>
        <Link
          to="/inbox"
          className={tab('inbox')}
          aria-current={active === 'inbox' ? 'page' : undefined}
          data-testid="mobile-tab-inbox"
        >
          <Icon name="inbox" size={20} />
          {inbox ? <span className="mobile-badge">{inbox.value}</span> : null}
          <span className="mobile-tab-label">{t('shell.nav.inbox')}</span>
        </Link>
        <button
          type="button"
          className="mobile-tab"
          onClick={openCommandPalette}
          data-testid="mobile-tab-search"
        >
          <Icon name="search" size={20} />
          <span className="mobile-tab-label">{t('shell.search')}</span>
        </button>
        <Link
          to={library}
          className={tab('library')}
          aria-current={active === 'library' ? 'page' : undefined}
          data-testid="mobile-tab-library"
        >
          <Icon name="folder" size={20} />
          <span className="mobile-tab-label">{t('shell.nav.library')}</span>
        </Link>
        <button
          type="button"
          className={`mobile-tab${menuActive ? ' active' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={menu}
          onClick={() => setMenu(true)}
          data-testid="mobile-tab-menu"
        >
          <Icon name="rows" size={20} />
          <span className="mobile-tab-label">{t('shell.nav.menu')}</span>
        </button>
      </nav>
      {menu ? (
        <Suspense fallback={null}>
          <MobileMenu open={menu} onClose={() => setMenu(false)} />
        </Suspense>
      ) : null}
    </>
  );
}
